import { Icon, INavButtonItem, INavLinkItem } from '@travelnest/hui';
import React from 'react';
import { NavigateFunction } from 'react-router-dom';

const isCurrentPath = (path: string) => {
	try {
		const here = new URL(window.location.href);
		return '/' + here.pathname.split('/')[1] === path;
	} catch (e) {
		console.warn(`Failed to detect current path`);
		return false;
	}
};

const notNull = <T, _>(val: T | null): val is T => {
	return val !== null;
};

export const getMenuItems = (
	navigate: NavigateFunction,
	path: string,
	isIncomplete: boolean,
	toggleFreshChatWidget: () => void,
	logout: () => void
): Array<(INavLinkItem | INavButtonItem)> => {
	return isIncomplete
		? [
			{
				label: 'Home',
				callback: () => navigate('/'),
				icon: <Icon name="Nav.Home" />,
				isHighlighted: path === '/'
			},
			{
				label: 'Help & support',
				url: 'https://travelnest.freshdesk.com/support/home',
				icon: <Icon name="Nav.Support" />,
			} as INavLinkItem,
			{
				label: 'Logout',
				callback: logout,
				icon: <Icon name="Nav.LogOut" />,
			}].filter(notNull)
		: [{
			label: 'Home',
			callback: () => navigate('/'),
			icon: <Icon name="Nav.Home" />,
			isHighlighted: path === '/'
		}, {
			label: 'Bookings',
			callback: () => navigate('/bookings'),
			icon: <Icon name="Nav.Bookings" />,
			isHighlighted: isCurrentPath('/bookings')
		}, {
			label: 'Messages',
			callback: () => window.location.assign(`${window.location.origin}/messages`),
			icon: <Icon name="Nav.Messages" />
		}, {
			label: 'Calendar',
			callback: () => navigate('/calendar'),
			icon: <Icon name="Nav.Calendar" />,
			isHighlighted: isCurrentPath('/calendar')
		}, {
			label: 'More',
			callback: () => navigate('/more'),
			icon: <Icon name="Nav.More" />,
			isHighlighted: isCurrentPath('/more')
				|| isCurrentPath('/account')
				|| isCurrentPath('/channels')
				|| isCurrentPath('/property-scores')
				|| isCurrentPath('/properties')
				|| isCurrentPath('/property')
		}].filter(notNull);
};

export const getNavigationItems = (
	navigate: NavigateFunction,
	path: string,
	isIncomplete: boolean,
	isLegacyCommission: boolean | undefined
): Array<(INavLinkItem | INavButtonItem)> => {
	return isIncomplete
		? [
			{
				label: 'Home',
				callback: () => navigate('/'),
				icon: <Icon name="Nav.Home" />,
				isHighlighted: path === '/',
			}
		]
		: [
			{
				label: 'Home',
				callback: () => navigate('/'),
				icon: <Icon name="Nav.Home" />,
				isHighlighted: path === '/',
			}, {
				label: 'Bookings',
				callback: () => navigate('/bookings'),
				icon: <Icon name="Nav.Bookings" />,
				isHighlighted: isCurrentPath('/bookings')
			}, {
				label: 'Messages',
				callback: () => window.location.assign(`${window.location.origin}/messages`),
				icon: <Icon name="Nav.Messages" />
			}, {
				label: 'Calendar',
				callback: () => navigate('/calendar'),
				icon: <Icon name="Nav.Calendar" />,
				isHighlighted: isCurrentPath('/calendar')
			}, {
				label: 'Direct',
				callback: () => window.location.assign(`${window.location.origin}/direct`),
				icon: <Icon name="Nav.Direct" />
			}, {
				label: 'Properties',
				callback: () => window.location.assign(`${window.location.origin}/properties/overview`),
				icon: <Icon name="Nav.Properties" />,
				isHighlighted: isCurrentPath('/properties') || isCurrentPath('/property')
			}, {
				label: 'Channels',
				callback: () => window.location.assign(`${window.location.origin}/channels`),
				icon: <Icon name="Nav.FeesAndChannels" />,
				isHighlighted: isCurrentPath('/channels')
			}, {
				label: 'Property score',
				callback: () => navigate('/property-scores'),
				icon: <Icon name="Nav.Score" />,
				isHighlighted: isCurrentPath('/property-scores')
			}].filter(notNull);
};

export const getSubNavigationItems = (
	navigate: NavigateFunction,
	path: string,
	isIncomplete: boolean,
	isAdmin: boolean,
	toggleFreshChatWidget: () => void,
	logout: () => void
): Array<(INavLinkItem | INavButtonItem)> => {
	const items = [
		!window.fcWidget ? null : {
			label: 'Chat to us',
			callback: () => toggleFreshChatWidget(),
			icon: <Icon name="Nav.ChatToUs" />,
		},
		{
			label: 'Help & support',
			url: 'https://travelnest.freshdesk.com/support/home',
			icon: <Icon name="Nav.Support" />,
		} as INavLinkItem,
		{
			label: 'My account',
			callback: () => navigate('/account/details'),
			icon: <Icon name="Nav.Account" />,
		},
		!isAdmin ? null : {
			label: 'Admin',
			callback: () => navigate('/admin'),
			icon: <Icon name="Nav.Support" />,
			isHighlighted: path.includes('/admin')
		},
		{
			label: 'Logout',
			callback: logout,
			icon: <Icon name="Nav.LogOut" />,
		}].filter(notNull);
	return isIncomplete
		? items.filter((n) => n.label !== 'My account')
		: items;
};
